import { useEffect, useMemo } from "react";
import { useNavigate, useParams } from "react-router";
import { useSelector } from "react-redux";

import Users from "containers/Users";
import Device from "containers/DeviceConfiguration/Device";
import ManufacturerAccounts from "containers/ManufacturerConfiguration/ManufacturerAccount";
import { CLINIC_ROLES, USER_ROLES } from "static/constants";
import { evokeManufacturers } from "static/manufacturers";
import useCheckRPMSaaSAccountUser from "hooks/useCheckRPMSaaSAccountUser";
import QuickTextTemplates from "pages/quickTextTemplates/QuickTextTemplates";
import LetterTemplateManagement from "containers/LetterManagement/LetterTemplateManagement";

const useSettings = (props) => {
  const { history } = props;

  const { view } = useParams();
  const navigate = useNavigate();
  const { currentUser } = useSelector((state) => state.auth);
  const { accountsMapper } = useSelector((state) => state.account);
  const currentTab = view ?? "users";

  const isRPMSaaSUser = useCheckRPMSaaSAccountUser();

  const hasDeviceConfigAccess = useMemo(() => {
    const { manufacturer: accessedManufacturers, role } = currentUser;
    const hasDeviceAccess = accessedManufacturers.some(
      (manufacturer) => !evokeManufacturers.includes(manufacturer)
    );
    const hasAccess = hasDeviceAccess && !CLINIC_ROLES.includes(role);

    return hasAccess;
  }, [currentUser]);

  const hasQuickTextTemplateAccess = useMemo(() => {
    const isClinicUser = CLINIC_ROLES.includes(currentUser.role);
    const hasAccess = currentUser.accounts.some(
      (account) =>
        accountsMapper?.[account]?.settings?.show_quick_text_templates
    );

    return isClinicUser && hasAccess;
  }, [accountsMapper, currentUser.accounts, currentUser.role]);

  const tabs = useMemo(() => {
    const baseTabs = [];
    if (
      [USER_ROLES.ADMIN, USER_ROLES.CLINIC_ADMIN, USER_ROLES.RPM_USER].includes(
        currentUser.role
      )
    ) {
      baseTabs.push({
        label: "Users",
        value: "users",
        component: <Users history={history} />,
      });
    }

    // Only add device/manufacturer tabs if not RPM SaaS user
    if (!isRPMSaaSUser && hasDeviceConfigAccess) {
      baseTabs.push({
        label: "Manufacturer Configuration",
        value: "manufacturerAccount",
        component: <ManufacturerAccounts />,
      });

      if (
        [USER_ROLES.ADMIN, USER_ROLES.CLINIC_ADMIN].includes(currentUser.role)
      ) {
        baseTabs.push({
          label: "Device Configuration",
          value: "devices",
          component: <Device />,
        });
      }
    }

    if (currentUser.user_prefs?.letter_template_config) {
      baseTabs.push({
        label: "Letter Templates",
        value: "letters",
        component: <LetterTemplateManagement />,
      });
    }

    if (hasQuickTextTemplateAccess) {
      baseTabs.push({
        label: "Quick Text Templates",
        value: "quick-text-templates",
        component: <QuickTextTemplates />,
      });
    }

    return baseTabs;
  }, [
    currentUser.role,
    hasDeviceConfigAccess,
    hasQuickTextTemplateAccess,
    history,
    isRPMSaaSUser,
  ]);

  useEffect(() => {
    if (tabs.length > 0 && !tabs.some((tab) => tab.value === currentTab)) {
      const firstTab = tabs[0];
      navigate(`/settings/${firstTab.value}`, { replace: true });
    }
  }, [tabs, currentTab, navigate]);

  const onTabChange = (newView) => {
    if (newView === currentTab) return;

    navigate(`/settings/${newView}`);
  };

  return { tabs, currentTab, onTabChange };
};

export default useSettings;
